import { InboxDeclinedError } from './errors.js';
import { formatPin } from './format.js';
import { t } from './i18n/index.js';
import { buildInboxPath } from './route.js';
import { fileMetaOf } from './send-file.js';
import { runSendSession } from './send-session.js';
import { createDropZone } from './sender-page.js';
import { SignalingClient, signalingUrl } from './signaling-client.js';
import { clear, el, hide, show } from './ui/dom.js';
import { createFileCard } from './ui/file-card.js';
import { createHeader, createErrorCard, userFacingError } from './ui/shell.js';
import { createStatusPill } from './ui/status.js';

type CustomerUi = Readonly<{
  status: ReturnType<typeof createStatusPill>;
  progressHost: HTMLElement;
}>;

/** Announces the file to the inbox, waits for the owner's decision, then streams like any sender. */
const runSendToInbox = async (inbox: string, file: File, ui: CustomerUi): Promise<void> => {
  const signaling = await SignalingClient.connect(signalingUrl(window.location));
  const peerJoined = signaling.waitFor((message): message is { type: 'peer-joined' } => message.type === 'peer-joined');
  const declined = signaling
    .waitFor((message): message is { type: 'inbox-declined' } => message.type === 'inbox-declined')
    .then(() => {
      throw new InboxDeclinedError('The inbox owner declined the file');
    });
  const created = signaling.waitFor(
    (message): message is { type: 'inbox-room-created'; roomId: string; ownerOnline: boolean } => message.type === 'inbox-room-created',
  );
  signaling.send({ type: 'create-for-inbox', inbox, file: fileMetaOf(file) });
  const { ownerOnline } = await created;
  ui.status.set('waiting', ownerOnline ? t('toInbox.status.waitingOnline') : t('toInbox.status.waitingOffline'));

  await runSendSession(file, signaling, Promise.race([peerJoined, declined]), {
    onReceiverConnected: () => ui.status.set('active', t('toInbox.status.accepted')),
    onWaitingForDestination: () => ui.status.set('active', t('sender.status.waitingDestination')),
    onTransferStart: (progress) => {
      ui.status.set('active', t('sender.status.beaming'));
      ui.progressHost.replaceChildren(progress.element);
    },
    onDelivered: () => {
      ui.status.set('success', t('sender.status.delivered'));
      ui.progressHost.append(el('a', { className: 'button button--large', href: buildInboxPath(inbox), text: t('sender.sendAnother') }));
    },
  });
  signaling.close();
};

export const renderSendToInboxPage = (root: HTMLElement, inbox: string): void => {
  clear(root);
  const main = el('main', { className: 'page' });
  const ui: CustomerUi = { status: createStatusPill(), progressHost: el('div') };
  const fileHost = el('div');
  const errorHost = el('div');
  hide(ui.status.element);

  const dropZone = createDropZone((file) => {
    clear(errorHost);
    hide(dropZone);
    fileHost.replaceChildren(createFileCard(fileMetaOf(file)));
    show(ui.status.element);
    ui.status.set('waiting', t('toInbox.status.announcing'));
    runSendToInbox(inbox, file, ui).catch((error: unknown) => {
      ui.status.set('error', t('status.failed'));
      errorHost.replaceChildren(createErrorCard(userFacingError(error), t('action.startOver'), () => window.location.reload()));
    });
  });

  main.append(createHeader(t('toInbox.tagline', { number: formatPin(inbox) })), dropZone, fileHost, ui.status.element, ui.progressHost, errorHost);
  root.append(main);
};
